import {
  Badge,
  Button,
  LegacyCard,
  Divider,
  DropZone,
  IndexTable,
  Layout,
  Link,
  Page,
  LegacyStack,
  Text,
  Thumbnail,
} from '@shopify/polaris';
import { HorizontalDotsMinor } from '@shopify/polaris-icons';
import { useCallback, useContext, useEffect, useState } from 'react';
import ToastContext from '../../../context/ToastContext';
import { getInventoryById, postInventoryFileEPH } from '../../../services/inventoriesService';
import { SET_ERROR, SET_OK } from '../../../context/ToastReducer';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { postCloseInventoryById } from '../../../services/inventoriesService';
import { deleteInventoryById } from '../../../services/inventoriesService';

const showInventorieEndpoint = (depositId, inventoryId, inventories) =>
  `/deposits/${depositId}/inventories/${inventoryId}/?r=${window.btoa(
    `/files/inventories/eph?i=${inventories.map((i) => `${i.deposit.id}-${i.id}`).join(',')}`,
  )}`;

const UploadInventoryFileEPH = () => {
  const [inventories, setInventories] = useState([]);
  const [hasInline, setHasInline] = useState(false);
  const history = useHistory();

  const handleFileCharged = async (newInventories) => {
    setInventories(newInventories);
  };

  useEffect(() => {
    const i = new URLSearchParams(window.location.search).get('i');
    setHasInline(!!i);
    if (i) {
      const ids = i.split(',');
      Promise.allSettled(ids.map((e) => e.split('-')).map((e) => getInventoryById(e[0], e[1]))).then((r) => {
        setInventories(r.filter((e) => e.status === 'fulfilled').map((i) => i.value.data));
      });
    }
  }, []);

  useEffect(() => {
    inventories.length > 0 && history.push(`?i=${inventories.map((i) => `${i.deposit.id}-${i.id}`).join(',')}`);
  }, [history, inventories]);

  return (
    <Page title="Inventario con fichero EPH" divider>
      {inventories.length === 0 && !hasInline && <FileInputState onFileCharged={handleFileCharged} />}
      {inventories.length > 0 && <ShowInventories inventories={inventories} onChangeInventories={setInventories} />}
    </Page>
  );
};

const FileInputState = ({ onFileCharged }) => {
  const [files, setFiles] = useState([]);
  const [, dispatchToast] = useContext(ToastContext);

  const handleDropZoneDrop = useCallback(
    (_dropFiles, acceptedFiles, _rejectedFiles) => setFiles((f) => [...f, ...acceptedFiles]),
    [],
  );

  const validImageTypes = ['text/plain'];

  const fileUpload = !files.length && <DropZone.FileUpload />;
  const uploadedFiles = files.length > 0 && (
    <LegacyStack vertical>
      {files.map((file, index) => (
        <LegacyStack alignment="center" key={index}>
          <Thumbnail
            size="small"
            alt={file.name}
            source={validImageTypes.includes(file.type) ? window.URL.createObjectURL(file) : HorizontalDotsMinor}
          />
          <div style={{ padding: 10 }}>
            {file.name}{' '}
            <Text variant="headingSm" as="h6">
              {file.size} bytes
            </Text>
          </div>
        </LegacyStack>
      ))}
    </LegacyStack>
  );

  useEffect(() => {
    files.forEach((f) => {
      const form = new FormData();
      form.append('file', f, f.name);

      postInventoryFileEPH(form)
        .then((r) => {
          dispatchToast({ type: SET_OK, payload: r.data });
          onFileCharged(r.data.inventories);
        })
        .catch((e) => {
          dispatchToast({ type: SET_ERROR, payload: e.response.data });
        })
        .finally(() => {
          setFiles([]);
        });
    });
    // eslint-disable-next-line
  }, [files]);

  return (
    <Layout>
      <Layout.Section secondary>
        <Text variant="bodyMd" as="p" color="subdued">
          Añada un fichero con el inventario de su tienda en formato CSV
        </Text>
      </Layout.Section>
      <Layout.Section>
        <LegacyCard sectioned>
          <DropZone onDrop={handleDropZoneDrop}>
            {uploadedFiles}
            {fileUpload}
          </DropZone>
        </LegacyCard>
      </Layout.Section>
    </Layout>
  );
};

const ShowInventories = ({ inventories, onChangeInventories }) => {
  const [rows, setRows] = useState([]);
  const [, dispatchToast] = useContext(ToastContext);

  const handleClose = (id) => {
    const i = inventories.find((i) => i.id === id);
    postCloseInventoryById(i.deposit.id, i.id)
      .then(async (r) => {
        dispatchToast({ type: SET_OK, payload: r.data });
        const newI = (await getInventoryById(i.deposit.id, i.id)).data;
        onChangeInventories(inventories.map((i) => (i.id === id ? newI : i)));
      })
      .catch((e) => {
        dispatchToast({ type: SET_ERROR, payload: e.response.data });
      });
  };

  const handleDelete = (id) => {
    const i = inventories.find((i) => i.id === id);
    deleteInventoryById(i.deposit.id, i.id)
      .then((r) => {
        dispatchToast({ type: SET_OK, payload: r.data });
        onChangeInventories(inventories.filter((i) => i.id !== id));
      })
      .catch((e) => {
        dispatchToast({ type: SET_ERROR, payload: e.response.data });
      });
  };

  const headingMarkup = [
    { title: 'Nombre' },
    {},
    { title: 'Dif. Productos', alignment: 'center' },
    { title: 'Dif. Variantes', alignment: 'center' },
    { title: 'Dif. Stock', alignment: 'end' },
    { title: 'Acciones', alignment: 'center' },
  ];

  useEffect(() => {
    inventories && setRows(inventories.map(rowMarkup));
    // eslint-disable-next-line
  }, [inventories]);

  const rowMarkup = (
    {
      id,
      status,
      distinctVariants,
      distinctProducts,
      distinctProductsExpected,
      stockQtyFound,
      distinctVariantsExpected,
      stockQtyExpected,
      createdAt,
      deposit,
    },
    index,
  ) => (
    <IndexTable.Row id={id} key={`r_${index}`} position={index}>
      <>
        <IndexTable.Cell>
          <Link monochrome url={showInventorieEndpoint(deposit.id, id, inventories)}>
            <Text variant="bodyMd" fontWeight="bold" as="span">
              {deposit.name}
            </Text>
          </Link>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <Badge status={status === 'DRAFT' ? 'info' : 'success'}>{status}</Badge>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <Text alignment="end">{distinctProductsExpected - distinctProducts}</Text>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <Text alignment="end">{distinctVariantsExpected - distinctVariants}</Text>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <Text alignment="end">{stockQtyExpected - stockQtyFound}</Text>
        </IndexTable.Cell>
        <IndexTable.Cell>
          {status !== 'CLOSED' && (
            <LegacyStack distribution="trailing">
              <Button outline size="slim" onClick={() => handleClose(id)}>
                Cerrar
              </Button>
              <Button outline destructive size="slim" onClick={() => handleDelete(id)}>
                Borrar
              </Button>
            </LegacyStack>
          )}
        </IndexTable.Cell>
      </>
    </IndexTable.Row>
  );

  return (
    <>
      {inventories && (
        <>
          <IndexTable
            resourceName={{
              singular: 'inventario',
              plural: 'inventarios',
            }}
            itemCount={rows.length}
            headings={headingMarkup}
            selectable={false}
          >
            {rows}
          </IndexTable>
          <Divider borderStyle="divider" />
        </>
      )}
    </>
  );
};

export default UploadInventoryFileEPH;
